import { generateId } from '@freelancer/datastore/testing/helpers';
import type {
  DisbursementMethodCurrency,
  DisbursementMethodType,
} from './disbursement-methods.backend-model';
import type { DisbursementMethod } from './disbursement-methods.model';
import type { AddressResponse } from '../customers/customers.backend-model';

export interface GenerateDisbursementMethodOptions {
  readonly id?: number;
  readonly accountName?: string;
  readonly accountType?: 'checking' | 'savings';
  readonly bankAbaRoutingNumber?: string;
  readonly bankAccountNumber?: string;
  readonly bankAddress?: AddressResponse;
  readonly bankBranchNumber?: string;
  readonly bankIban?: string;
  readonly bankName?: string;
  readonly bankSortCode?: string;
  readonly bankSwiftCode?: string;
  readonly mainBranchSwiftcode?: string;
  readonly currency?: DisbursementMethodCurrency;
  readonly type?: DisbursementMethodType;
  readonly isDefault?: boolean;
  readonly isValid?: boolean;
}

export function generateDisbursementMethodObject({
  id,
  accountName,
  accountType,
  bankAbaRoutingNumber,
  bankAccountNumber,
  bankAddress,
  bankBranchNumber,
  bankIban,
  bankName,
  bankSortCode,
  bankSwiftCode,
  mainBranchSwiftcode,
  currency,
  type,
  isDefault,
  isValid,
}: GenerateDisbursementMethodOptions = {}): DisbursementMethod {
  return {
    id: id ?? generateId(),
    accountName: accountName ?? 'Test User',
    accountType,
    bankAbaRoutingNumber,
    bankAccountNumber: bankAccountNumber ?? '0389174625',
    bankAddress: bankAddress ?? { country: 'US' },
    bankBranchNumber,
    bankIban,
    bankName: bankName ?? 'Test Bank',
    bankSortCode,
    bankSwiftCode,
    mainBranchSwiftcode,
    currency: currency ?? 'usd',
    type: type ?? 'wire_transfer',
    isDefault,
    isValid: isValid ?? true,
  };
}

export function generateDisbursementMethodObjects(
  methods: readonly GenerateDisbursementMethodOptions[],
): readonly DisbursementMethod[] {
  return methods.map(method => generateDisbursementMethodObject(method));
}

export function defaultDisbursementMethods(): readonly DisbursementMethod[] {
  return [
    {
      accountName: 'Test User',
      bankAccountNumber: '0389174625',
      bankAddress: {
        country: 'PH',
      },
      bankName: 'BPI',
      bankSwiftCode: 'TLBPPHMMXXX',
      mainBranchSwiftcode: 'TLBPPHMM',
      currency: 'usd',
      id: 1_718_211,
      type: 'wire_transfer',
      isValid: true,
    },
    {
      accountName: 'Test User',
      bankAccountNumber: '10483927',
      bankAddress: {
        country: 'AU',
      },
      bankBranchNumber: '062345',
      bankName: 'Commonwealth Bank of Australia',
      bankSortCode: '062345',
      bankSwiftCode: 'CTBAAU2SXXX',
      mainBranchSwiftcode: 'CTBAAU2S',
      currency: 'aud',
      id: 1_718_219,
      type: 'wire_transfer',
      isValid: true,
    },
    {
      accountName: 'Test User',
      accountType: 'savings',
      bankAbaRoutingNumber: '084726351',
      bankAccountNumber: '900174628519',
      bankAddress: {
        country: 'US',
      },
      bankName: 'Redwood National Bank',
      currency: 'usd',
      id: 1_718_220,
      type: 'ach',
      isValid: true,
    },
    {
      accountName: 'Test User',
      bankAbaRoutingNumber: '114923756',
      bankAccountNumber: '628490173552',
      bankAddress: {
        country: 'US',
      },
      bankName: 'Silverline Community Bank',
      currency: 'usd',
      id: 1_718_230,
      type: 'wire_transfer',
      isValid: true,
    },
    {
      accountName: 'Test User',
      bankAccountNumber: '58394726',
      bankAddress: {
        country: 'GB',
      },
      bankBranchNumber: '407219',
      bankIban: 'GB82ATBK40721958394726',
      bankName: 'Albion Trust Bank',
      bankSortCode: '407219',
      bankSwiftCode: 'ATBKGB2L',
      mainBranchSwiftcode: 'ATBKGB2L',
      currency: 'gbp',
      id: 1_718_244,
      type: 'wire_transfer',
      isValid: true,
    },
    {
      accountName: 'Test User',
      bankAccountNumber: '091234567890',
      bankAddress: {
        country: 'PH',
      },
      bankName: 'PH Euro Bank',
      bankSwiftCode: 'DEMOPHMMXXX',
      mainBranchSwiftcode: 'DEMOPHMM',
      currency: 'euro',
      id: 1_718_317,
      type: 'wire_transfer_international',
      isValid: true,
    },
  ];
}
